import { useMemo } from "react";
import useSWR, {
  type KeyedMutator,
  type SWRConfiguration,
  type SWRHook,
} from "swr";
import {
  AuthTokenHeader,
  FetcherOptions,
  FetcherResult,
  fetcherThrows,
} from "./fetcher";

type SWRKey = Parameters<SWRHook>[0];

export interface UseFetchState<
  T,
  U = Record<string, unknown>,
> extends FetcherResult<T, U> {
  refetch: KeyedMutator<T>;
  isLoading: boolean;
  isValidating: boolean;
}

export interface UseFetchStateForSuspense<
  T,
  U = Record<string, unknown>,
> extends UseFetchState<T, U> {
  data: T;
}

interface UseFetchKey {
  url: string;
  token: string | undefined;
  body: RequestInit["body"] | undefined;
  dependencies: SWRKey | undefined;
}

interface UseFetchOptions<T> extends Omit<FetcherOptions, "fetch"> {
  /**
   * keyの型を自動で反映させたければジェネリクスなどを利用してoptions.keyの型を持ってくる必要があるが、
   * fetchを上書きしたいケースがそこまであるとは思えないため、fetchの引数の型を緩く指定している。
   */
  fetch?: (args: UseFetchKey) => Promise<T>;
  swrConfig?: Partial<SWRConfiguration> & {
    fallbackData?: never;
    suspense?: false;
  };
  authTokenHeader?: AuthTokenHeader;
  /**
   * 依存関係の変更を検知して再フェッチを行いたい場合に指定する。
   */
  dependencies?: SWRKey;
}

interface UseFetchOptionsWithSuspense<T> extends Omit<
  UseFetchOptions<T>,
  "swrConfig"
> {
  swrConfig: Partial<SWRConfiguration> &
    (
      | {
          fallbackData: unknown;
        }
      | {
          suspense: true;
          fallbackData?: unknown;
        }
    );
}

interface UseFetch {
  <T, U = Record<string, unknown>>(
    url: string | null,
    options?: UseFetchOptions<T>,
  ): UseFetchState<T, U>;
  <T, U = Record<string, unknown>>(
    url: string | null,
    options?: UseFetchOptionsWithSuspense<T>,
  ): UseFetchStateForSuspense<T, U>;
}

export const useFetch: UseFetch = (url: string | null, options = {}) => {
  type T = ReturnType<UseFetch>["data"];
  type E = ReturnType<UseFetch>["error"];

  const key = useMemo((): UseFetchKey | null => {
    if (url === null) {
      return url;
    } else {
      return {
        url,
        token: options.token,
        body: options.body,
        dependencies: options.dependencies,
      };
    }
  }, [options.body, options.dependencies, options.token, url]);
  const fetcherWrapper = useMemo(
    () =>
      options.fetch ??
      (({ url, token }: { url: string; token?: string }) => {
        return fetcherThrows<T>(url, {
          method: options.method,
          token,
          body: options.body,
          authTokenHeader: options.authTokenHeader,
        });
      }),
    [options.authTokenHeader, options.body, options.fetch, options.method],
  );

  const { data, error, mutate, isLoading, isValidating } = useSWR<T, E>(
    key,
    fetcherWrapper,
    options.swrConfig,
  );

  return {
    data,
    error,
    refetch: mutate,
    isLoading,
    isValidating,
  };
};

interface UseAuthFetch {
  <T, U = Record<string, unknown>>(
    token: string,
    url: string | null,
    options?: UseFetchOptions<T>,
  ): UseFetchState<T, U>;
  <T, U = Record<string, unknown>>(
    token: string,
    url: string | null,
    options?: UseFetchOptionsWithSuspense<T>,
  ): UseFetchStateForSuspense<T, U>;
}

export const useAuthFetch: UseAuthFetch = (
  token: string,
  url: string | null,
  options = {},
) => {
  return useFetch(url === null ? null : token ? url : null, {
    token,
    authTokenHeader: "X-Access-Token",
    ...options,
    //@ts-expect-error UseFetchOptionsWithSuspenseにUseFetchOptionsのswrConfigを渡すことが出来ないので型エラーが出る。これをランタイムに影響を出さずにケアするのが難しいため、このエラーは無視する。
    swrConfig: {
      ...(options.swrConfig ?? {}),
    },
  });
};

// Authorization: Bearer [TOKEN]の形式でセットするuseAuthFetch
export const useAuthBearerFetch: UseAuthFetch = (
  token: string,
  url: string | null,
  options = {},
) => {
  return useAuthFetch(token, url, {
    authTokenHeader: "Authorization",
    ...options,
    //@ts-expect-error UseFetchOptionsWithSuspenseにUseFetchOptionsのswrConfigを渡すことが出来ないので型エラーが出る。これをランタイムに影響を出さずにケアするのが難しいため、このエラーは無視する。
    swrConfig: {
      ...(options.swrConfig ?? {}),
    },
  });
};
